import React, { memo } from "react";

import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { Company, EntityType, EntityTypeSupportingCard } from "@mapmycustomers/shared/types/entity";

import AddOrCreateDropdown from "@app/component/preview/components/AddOrCreateDropdown";
import Section from "@app/component/preview/components/Section";

import styles from "../../Relationships.module.scss";
import EditRelatedCompanies from "../EditRelatedCompanies";

const messages = defineMessages({
  addCompany: {
    id: "entityPreview.relationships.parentCompany.noData.restEntityTypes",
    defaultMessage: "Add a company",
    description: "Text to show when there's no company yet in Company Record pane",
  },
  addParentCompany: {
    id: "entityPreview.relationships.parentCompany.noData.forCompany",
    defaultMessage: "Add a parent company",
    description: "Text to show when there's no parent company yet in Company Record pane",
  },
  parentCompany: {
    id: "entityPreview.relationships.parentCompany.forCompany",
    defaultMessage: "Parent Company",
    description: "Parent company section title for companies of the preview record pane",
  },
  regularName: {
    id: "entityPreview.relationships.parentCompany.regularName",
    defaultMessage: "Company",
    description: "Parent company section title for rest entity types of the preview record pane",
  },
});

interface Props {
  addCompanyParentCompanyLoading?: boolean;
  company?: Company;
  entityType: EntityTypeSupportingCard;
  onAddExisting?: () => void;
  onAddNew: () => void;
  onFetchMoreCompanies?: () => void;
  onRemove?: (company: Company) => void;
  totalCompanies: number;
}

const ParentCompany: React.FC<Props> = ({
  addCompanyParentCompanyLoading = false,
  company,
  entityType,
  onAddExisting,
  onAddNew,
  onFetchMoreCompanies,
  onRemove,
  totalCompanies,
}) => {
  const intl = useIntl();

  return (
    <Section
      action={
        !company ? (
          <AddOrCreateDropdown
            loading={addCompanyParentCompanyLoading}
            onAddExisting={onAddExisting}
            onAddNew={onAddNew}
          />
        ) : null
      }
      title={intl.formatMessage(
        entityType === EntityType.COMPANY ? messages.parentCompany : messages.regularName
      )}
      titleClassName={styles.sectionTitle}
    >
      <div className={cn(styles.container, { [styles.noParent]: !company })}>
        <EditRelatedCompanies
          companies={[company].filter(Boolean) as Company[]}
          noDataTitle={intl.formatMessage(
            entityType === EntityType.COMPANY ? messages.addParentCompany : messages.addCompany
          )}
          onFetchMoreCompanies={onFetchMoreCompanies}
          onRemove={onRemove}
          parentCompaniesFiltered={[company].filter(Boolean) as Company[]}
          totalCompanies={totalCompanies}
        />
        {/* a dummy div to make grid repeat(auto-fit) work correctly */}
        <div />
      </div>
    </Section>
  );
};

export default memo(ParentCompany);
